import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function KeyMetrics({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-4xl mx-auto">
        
        <Button 
          variant="ghost" 
          onClick={() => navigate('/learn')} 
          className="mb-6 dark:text-gray-300"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Learn Home
        </Button>

        <Card className="glass p-8 mb-6 dark:bg-gray-800">
          <div className="flex items-center space-x-2 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              Intermediate
            </span>
            <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-4 h-4 mr-1" />
              18 min read
            </span>
          </div>

          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            📉 Key Metrics: P/E, ROE, Debt/Equity
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Deep dive into the most important financial ratios for stock evaluation
          </p>

          <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
            
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Why Financial Ratios Matter</h2>
              <p className="mb-4">
                Reading raw financial statements is good, but <strong>ratios</strong> make comparison easy. 
                Would you rather compare "₹50,000 Cr profit vs ₹5,000 Cr profit" or know which company is more efficient?
              </p>
              <p className="mb-4">
                Financial ratios help you quickly evaluate and compare companies, regardless of their size. 
                Let's master the most important ones!
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">1. P/E Ratio (Price-to-Earnings) - The Most Popular Metric</h2>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg mb-4">
                <p className="text-sm mb-2"><strong>Formula:</strong></p>
                <p className="text-lg font-mono font-bold text-center py-2">P/E = Current Share Price ÷ Earnings Per Share (EPS)</p>
                <p className="text-sm mt-2"><strong>What it means:</strong> How many years of current earnings you're paying for when you buy the stock.</p>
              </div>

              <div className="mb-4">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Real Example:</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded">
                    <p className="font-bold text-gray-800 dark:text-gray-100">TCS</p>
                    <p className="text-sm">Share Price: ₹3,500</p>
                    <p className="text-sm">EPS: ₹120</p>
                    <p className="text-sm font-bold text-green-600 dark:text-green-400">P/E = 29</p>
                  </div>
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded">
                    <p className="font-bold text-gray-800 dark:text-gray-100">Infosys</p>
                    <p className="text-sm">Share Price: ₹1,400</p>
                    <p className="text-sm">EPS: ₹60</p>
                    <p className="text-sm font-bold text-purple-600 dark:text-purple-400">P/E = 23</p>
                  </div>
                </div>
                <p className="text-sm mt-3 italic">
                  Infosys has a lower P/E (23 vs 29), meaning it's relatively cheaper compared to its earnings. 
                  But lower isn't always better - you need to consider growth!
                </p>
              </div>

              <div className="space-y-2">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded">
                  <p className="text-sm"><strong>✅ Low P/E (below 15):</strong> Stock might be undervalued OR company has problems</p>
                </div>
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <p className="text-sm"><strong>⚠️ Medium P/E (15-25):</strong> Fairly valued in most cases</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded">
                  <p className="text-sm"><strong>🔴 High P/E (above 30):</strong> Expensive OR market expects high growth (like tech stocks)</p>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded mt-4 border-l-4 border-yellow-500">
                <p className="text-sm">
                  <strong>⚠️ Pro Tip:</strong> Always compare P/E with industry peers, not across different sectors. 
                  IT companies typically have higher P/E than banks or manufacturing.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">2. ROE (Return on Equity) - Quality Metric</h2>
              
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg mb-4">
                <p className="text-sm mb-2"><strong>Formula:</strong></p>
                <p className="text-lg font-mono font-bold text-center py-2">ROE = (Net Profit ÷ Shareholder Equity) × 100</p>
                <p className="text-sm mt-2"><strong>What it means:</strong> How efficiently the company uses shareholders' money to generate profits.</p>
              </div>

              <div className="mb-4">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Indian Examples:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="p-3 text-left">Company</th>
                        <th className="p-3 text-right">Net Profit</th>
                        <th className="p-3 text-right">Equity</th>
                        <th className="p-3 text-right">ROE</th>
                        <th className="p-3 text-center">Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/10">
                        <td className="p-3">Asian Paints</td>
                        <td className="p-3 text-right">₹3,200 Cr</td>
                        <td className="p-3 text-right">₹10,000 Cr</td>
                        <td className="p-3 text-right font-bold">32%</td>
                        <td className="p-3 text-center">⭐⭐⭐</td>
                      </tr>
                      <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/10">
                        <td className="p-3">TCS</td>
                        <td className="p-3 text-right">₹42,000 Cr</td>
                        <td className="p-3 text-right">₹1,00,000 Cr</td>
                        <td className="p-3 text-right font-bold">42%</td>
                        <td className="p-3 text-center">⭐⭐⭐</td>
                      </tr>
                      <tr className="border-t dark:border-gray-600">
                        <td className="p-3">Tata Steel</td>
                        <td className="p-3 text-right">₹5,000 Cr</td>
                        <td className="p-3 text-right">₹60,000 Cr</td>
                        <td className="p-3 text-right font-bold">8.3%</td>
                        <td className="p-3 text-center">⭐</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm mt-3 italic">
                  Notice: Asset-light businesses (TCS, Asian Paints) have higher ROE than capital-intensive ones (Tata Steel). 
                  This is why Warren Buffett loves asset-light businesses!
                </p>
              </div>

              <div className="space-y-2">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded">
                  <p className="text-sm"><strong>✅ ROE above 15%:</strong> Excellent company, efficiently using capital</p>
                </div>
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <p className="text-sm"><strong>⚠️ ROE 10-15%:</strong> Decent, but not exceptional</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded">
                  <p className="text-sm"><strong>🔴 ROE below 10%:</strong> Struggling to generate returns, avoid unless there's a turnaround story</p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded mt-4">
                <p className="text-sm">
                  <strong>👉 Warren Buffett's Rule:</strong> He looks for companies with ROE consistently above 15% for 10+ years. 
                  This indicates a strong competitive advantage (moat).
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">3. Debt-to-Equity Ratio - Safety Check</h2>
              
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg mb-4">
                <p className="text-sm mb-2"><strong>Formula:</strong></p>
                <p className="text-lg font-mono font-bold text-center py-2">D/E = Total Debt ÷ Shareholder Equity</p>
                <p className="text-sm mt-2"><strong>What it means:</strong> How much the company relies on borrowed money vs owners' money.</p>
              </div>

              <div className="mb-4">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Contrasting Examples:</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded border-2 border-green-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100">TCS (Low Debt)</p>
                    <p className="text-sm">Debt: ₹7,000 Cr</p>
                    <p className="text-sm">Equity: ₹80,000 Cr</p>
                    <p className="text-sm font-bold text-green-600 dark:text-green-400">D/E = 0.09 ✅</p>
                    <p className="text-xs mt-2 italic">Very safe, minimal financial risk</p>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border-2 border-red-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100">Vodafone Idea (High Debt)</p>
                    <p className="text-sm">Debt: ₹1,90,000 Cr</p>
                    <p className="text-sm">Equity: ₹-60,000 Cr (negative!)</p>
                    <p className="text-sm font-bold text-red-600 dark:text-red-400">D/E = Negative ❌</p>
                    <p className="text-xs mt-2 italic">Extreme risk, bankruptcy possible</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded">
                  <p className="text-sm"><strong>✅ D/E below 0.5:</strong> Very safe, low financial risk (ideal for conservative investors)</p>
                </div>
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <p className="text-sm"><strong>⚠️ D/E 0.5-1.0:</strong> Moderate debt, acceptable for most companies</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded">
                  <p className="text-sm"><strong>🔴 D/E above 2.0:</strong> High risk! Company is heavily leveraged, vulnerable in downturns</p>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded mt-4 border-l-4 border-yellow-500">
                <p className="text-sm">
                  <strong>⚠️ Important:</strong> Capital-intensive industries (like airlines, real estate, telecom) naturally have higher debt. 
                  Compare D/E with industry peers, not across sectors.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Bonus Metrics Every Investor Should Know</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">EPS (Earnings Per Share)</h4>
                  <p className="text-sm mb-1">Net Profit ÷ Number of Shares</p>
                  <p className="text-sm"><strong>Why it matters:</strong> Shows profit per share. Look for consistent EPS growth over 5-10 years.</p>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">P/B Ratio (Price-to-Book)</h4>
                  <p className="text-sm mb-1">Market Price ÷ Book Value per Share</p>
                  <p className="text-sm"><strong>Why it matters:</strong> Below 1 = trading below asset value. Value investors love P/B &lt; 1.5.</p>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">Dividend Yield</h4>
                  <p className="text-sm mb-1">(Annual Dividend per Share ÷ Share Price) × 100</p>
                  <p className="text-sm"><strong>Why it matters:</strong> Shows % return from dividends alone. 3-5% is healthy for Indian blue chips.</p>
                </div>

                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">Current Ratio</h4>
                  <p className="text-sm mb-1">Current Assets ÷ Current Liabilities</p>
                  <p className="text-sm"><strong>Why it matters:</strong> Measures ability to pay short-term obligations. Above 1.5 is safe.</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Putting It All Together - TCS vs Tata Steel</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Metric</th>
                      <th className="p-3 text-center">TCS</th>
                      <th className="p-3 text-center">Tata Steel</th>
                      <th className="p-3 text-left">Verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">P/E Ratio</td>
                      <td className="p-3 text-center">29</td>
                      <td className="p-3 text-center">8</td>
                      <td className="p-3">TCS expensive, Tata Steel cheap (or has issues?)</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">ROE</td>
                      <td className="p-3 text-center text-green-600 font-bold">42%</td>
                      <td className="p-3 text-center">8%</td>
                      <td className="p-3">TCS far more efficient at generating returns</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">D/E Ratio</td>
                      <td className="p-3 text-center text-green-600 font-bold">0.09</td>
                      <td className="p-3 text-center text-red-600">1.8</td>
                      <td className="p-3">TCS much safer, Tata Steel has high debt risk</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-blue-50 dark:bg-blue-900/10">
                      <td className="p-3 font-bold">Overall</td>
                      <td className="p-3 text-center font-bold text-green-600">⭐⭐⭐</td>
                      <td className="p-3 text-center font-bold">⭐</td>
                      <td className="p-3 font-bold">TCS: Quality company. Tata Steel: Cyclical, risky</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-700">
              <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-3">💡 Key Takeaways</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>P/E Ratio:</strong> Lower is cheaper, but compare with industry peers and growth rate</li>
                <li><strong>ROE above 15%:</strong> Sign of quality business with competitive advantage</li>
                <li><strong>D/E below 1:</strong> Safe company, can weather economic downturns</li>
                <li><strong>Never use one metric alone:</strong> Look at all ratios together for complete picture</li>
                <li><strong>Compare over time:</strong> Check if metrics are improving or declining over 5-10 years</li>
                <li><strong>Use Screener.in:</strong> All these ratios are pre-calculated for you!</li>
              </ul>
            </div>

          </div>
        </Card>

        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/learn/financial-statements')}
            className="dark:bg-gray-800 dark:text-gray-100"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous: Financial Statements
          </Button>
          <Button 
            onClick={() => navigate('/learn/intrinsic-value')}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
          >
            Next: Intrinsic Value
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

      </div>
      <Footer />
    </div>
  );
}
